import * as vp from 'vopay';
import { FunctionComponent, JSX } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type TransactionInfoProps = {
  transaction: vp.FedwireTransactionResponse;
  Notes: string;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

type FC = FunctionComponent<TransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetail: () => vp.TableProps;
  accountHolderDetails: () => vp.TableProps;
  bankAccountDetails: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
};

const FedwireFundingTransactionInfo: FC = (props: TransactionInfoProps) => {
  const { state } = useLocation<DataProps>();
  const { transaction, Notes } = props;
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.FedwireFundingTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsHeader') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetail')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('accountHolderDetailsHeader') || ''} />
      </Box>
      <Table {...getTableProps('accountHolderDetails')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('bankAccountDetailsHeader') || ''} />
      </Box>
      <Table {...getTableProps('bankAccountDetails')} />
      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoHeader') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}
    </>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetail: () => {
        const transactionDetailsPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionID'),
            value: transaction.TransactionID,
          },
          {
            display: t('transactionDetails.transactionStatus'),
            value: (
              <Badge
                label={tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                  defaultValue: transaction.TransactionStatus,
                })}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
          },
          {
            display: t('transactionDetails.transactionDate'),
            value: transaction.TransactionDateTime,
          },
          {
            display: t('transactionDetails.lastModified'),
            value: transaction.LastModified,
          },
          {
            display: t('transactionDetails.holdReleaseDate'),
            value: transaction.HoldReleaseDateTime,
          },
          {
            display: t('transactionDetails.type'),
            value:
              tGlobal('Transaction.TransactionHistory.transactionTypes.Fedwire Funding', {
                defaultValue: 'Fedwire Funding',
              }) || '',
          },
          {
            display: t('transactionDetails.wireReferenceNumber'),
            value: transaction.WireReferenceNumber,
          },
          {
            display: t('transactionDetails.amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            display: t('transactionDetails.holdAmount'),
            value: `$${formattedCurrency(transaction.HoldAmount)}`,
          },
          {
            display: t('transactionDetails.currency'),
            value: transaction.Currency,
          },
          {
            display: t('transactionDetails.notes'),
            value: Notes,
          },
        ];

        const transactionDetailsProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: transactionDetailsPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return transactionDetailsProps;
      },
      accountHolderDetails: () => {
        const accountHolderDetailsPairs: KeyValuePair[] = [
          {
            display: t('accountHolderDetails.name'),
            value: `${transaction.FirstName || ''} ${transaction.LastName || ''}`,
            visible: !!transaction.FirstName,
          },
          {
            display: t('accountHolderDetails.companyName'),
            value: transaction.CompanyName || '',
            visible: !!transaction.CompanyName,
          },
          {
            display: t('accountHolderDetails.address'),
            value: `${transaction.Address1} ${transaction.Address2 || ''}`,
            visible: transaction.Address1 !== '',
          },
          {
            display: t('accountHolderDetails.city'),
            value: transaction.City,
            visible: transaction.City !== '',
          },
          {
            display: t('accountHolderDetails.postalCode'),
            value: transaction['PostalCode/Zipcode'],
            visible: !!transaction['PostalCode/Zipcode'],
          },
          {
            display: t('accountHolderDetails.provinceState'),
            value: transaction['Province/State'],
            visible: !!transaction['Province/State'],
          },
          {
            display: t('accountHolderDetails.country'),
            value: transaction.Country,
            visible: true,
          },
        ];

        const accountHolderDetailsProps: vp.TableProps = {
          name: 'account-holder-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('accountHolderDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountHolderDetailsPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountHolderDetailsProps;
      },
      bankAccountDetails: () => {
        const bankingInformationPairs: KeyValuePair[] = [
          {
            display: t('bankAccountDetails.abaRoutingNumber'),
            value: transaction.ABARoutingNumber,
          },
          {
            display: t('bankAccountDetails.accountNumber'),
            value: transaction.AccountNumber,
          },
        ];

        const bankingInformationProps: vp.TableProps = {
          name: 'banking-information',
          variant: 'pair',
          columns: {
            detailKey: { header: t('bankAccountDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: bankingInformationPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return bankingInformationProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('errorInfo.failureDateTime'),
            value: transaction.LastModified,
          },
          {
            display: t('errorInfo.amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            display: t('errorInfo.failureReason'),
            value: state.failureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInfo.header') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default FedwireFundingTransactionInfo;
